import React from 'react';
import { MedicalDataTypes } from '../../../primary-record-types';
import { View } from 'react-native';
import { getLabValueValueDisplay, isOutOfRange } from '../../../utils/medicalDataUtils';
import Text from '../../v2BaseComponents/Text';
import { colors } from '../../../utils/theme';

export type ValueIndicatorOwnProps = {
    containerWidth: number;
    labValue: MedicalDataTypes.LabValue;
    height: number;
    valuePercentage: number;
    indicatorSize: number;
};

export type ValueIndicatorProps = ValueIndicatorOwnProps;

const ValueIndicator: React.FC<ValueIndicatorProps> = ({
    containerWidth,
    labValue,
    height,
    valuePercentage,
    indicatorSize,
}) => {
    const [textWidth, setTextWidth] = React.useState<number | null>(null);

    const getIndicatorLayout = () => {
        if (!textWidth) {
            return {
                textLeft: 0,
                indicatorLeft: 0,
            };
        }

        const containerWidthOffset = textWidth / containerWidth / 2; // position from center of div
        const unClippedLeft = valuePercentage - containerWidthOffset;
        const textLeft = Math.min(Math.max(0, unClippedLeft), 1 - textWidth / containerWidth);
        const indicatorLeft = valuePercentage - indicatorSize / containerWidth;
        return {
            textLeft: textLeft * 100,
            indicatorLeft: indicatorLeft * 100,
        };
    };

    const { textLeft, indicatorLeft } = getIndicatorLayout();
    const backgroundColor = isOutOfRange(labValue) ? colors['Primary-Reg'] : colors.Success;

    return (
        <View
            style={{
                width: containerWidth,
                height,
                position: 'relative',
                top: 0,
                left: 0,
                right: 0,
                overflow: 'hidden',
            }}
        >
            <View
                style={{
                    padding: 2,
                    position: 'absolute',
                    top: 0,
                    height: height - indicatorSize,
                    left: `${textLeft}%`,
                    opacity: textWidth === null ? 0 : 1, // fade in after text width is known
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor,
                    borderRadius: 5,
                }}
                testID="range-bar-indicator"
                onLayout={({ nativeEvent: { layout } }) => {
                    setTextWidth(layout.width); // there seems to be a rounding error that will wrap text if there isn't a bit of a buffer
                }}
            >
                <View style={{ padding: 4 }}>
                    <Text variant="subtitle-2" color={isOutOfRange(labValue) ? 'Dark-Text' : 'White'}>
                        {getLabValueValueDisplay(labValue)}
                    </Text>
                </View>
            </View>
            <View
                style={{
                    width: indicatorSize,
                    height: indicatorSize,
                    position: 'absolute',
                    top: height - indicatorSize,
                    left: `${indicatorLeft}%`,
                    backgroundColor: 'transparent',
                    borderStyle: 'solid',
                    borderLeftWidth: indicatorSize,
                    borderRightWidth: indicatorSize,
                    borderBottomWidth: (Math.sqrt(3) / 2) * indicatorSize,
                    borderLeftColor: 'transparent',
                    borderRightColor: 'transparent',
                    borderBottomColor: backgroundColor,
                    transform: [{ rotate: '180deg' }], // Rotate the triangle to point down
                }}
                testID="range-bar-indicator-triangle"
            />
        </View>
    );
};

export default React.memo(ValueIndicator);
